import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

import type { ChartTokens } from "./useChartTokens";

interface Props {
    names: string[];
    values: number[];
    tokens: ChartTokens;
    seriesName: string;
    formatValue: (value: number) => string;
    ariaLabel: string;
}

/**
 * One measure across a few named things — sessions per professional, per kind
 * of session. One series, so no legend: the card's title names it, and each
 * bar carries its number at the end.
 */
export default function HorizontalBarChart({
    names,
    values,
    tokens,
    seriesName,
    formatValue,
    ariaLabel,
}: Props) {
    const options: ApexOptions = {
        chart: {
            type: "bar",
            fontFamily: "inherit",
            toolbar: { show: false },
            animations: { enabled: true, speed: 400 },
        },
        colors: [tokens.series1],
        plotOptions: {
            bar: {
                horizontal: true,
                borderRadius: 4,
                borderRadiusApplication: "end",
                barHeight: "60%",
                dataLabels: { position: "top" },
            },
        },
        stroke: { show: true, width: 2, colors: [tokens.surface] },
        dataLabels: {
            enabled: true,
            formatter: (value) => formatValue(Number(value)),
            offsetX: 22,
            style: { fontSize: "12px", fontWeight: 500, colors: [tokens.ink] },
        },
        legend: { show: false },
        grid: {
            xaxis: { lines: { show: true } },
            yaxis: { lines: { show: false } },
            padding: { right: 24 },
        },
        xaxis: {
            categories: names,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: { formatter: (value) => formatValue(Number(value)) },
        },
        yaxis: { labels: { maxWidth: 180 } },
        tooltip: {
            theme: "light",
            y: { formatter: (value) => formatValue(value) },
        },
    };

    return (
        <div role="img" aria-label={ariaLabel} className="-mx-2">
            <Chart
                options={options}
                series={[{ name: seriesName, data: values }]}
                type="bar"
                height={Math.max(140, 56 + names.length * 40)}
                width="100%"
            />
        </div>
    );
}
